'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { FC, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import FadeIn from '@/components/FadeIn/FadeIn';
import {
  HOME_PRODUCT_TABS,
  HOME_TAB_DEMO_VIDEOS,
  HomeProductCard,
  ProductTabId,
} from '@/constants/homeProductTabs';
import ProductDetailModal from './ProductDetailModal';

const ACCENT_COLOR = '#094FFE';

const PlayIcon: FC = () => (
  <svg
    width='13'
    height='13'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden
  >
    <path d='M8 5v14l11-7z' />
  </svg>
);

const ProductCard: FC<{
  card: HomeProductCard;
  size: 'large' | 'small' | 'wide';
  onOpen: (card: HomeProductCard) => void;
}> = ({ card, size, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(hover: hover)');
    setCanHover(mq.matches);
    const handler = (e: MediaQueryListEvent) => setCanHover(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return (
  <button
    type='button'
    onClick={() => onOpen(card)}
    onMouseEnter={() => canHover && setHovered(true)}
    onMouseLeave={() => canHover && setHovered(false)}
    className='relative flex w-full flex-col rounded-3xl sm:rounded-[20px] border border-[#EFEFEF] bg-white p-4 sm:p-6 text-left'
    style={{
      transform: hovered ? 'scale(1.01)' : 'scale(1)',
      boxShadow: hovered ? '0 8px 30px rgba(0,0,0,0.10)' : 'none',
      transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 350ms ease-out',
    }}
  >
    <span
      className='absolute right-4 top-4 z-[2] flex h-8 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors sm:right-6 sm:top-6'
      style={{
        backgroundColor: hovered ? '#094FFE' : '#F2F5FF',
        color: hovered ? '#ffffff' : '#0848E7',
      }}
      aria-hidden
    >
      <PlayIcon />
      Watch demo
    </span>

    <h3
      className='max-w-[62%] font-medium leading-[1.3] text-black sm:max-w-none sm:pr-32'
      style={{
        fontSize:
          size === 'wide'
            ? 'clamp(1.25rem, 5vw, 1.5rem)'
            : size === 'large'
              ? 'clamp(1.25rem, 5vw, 1.5rem)'
              : 'clamp(1rem, 4vw, 1.375rem)',
      }}
    >
      {card.title}
    </h3>
    {card.blurb && (
      <p className='mt-2 max-w-[62%] text-sm leading-6 text-slate-600 sm:max-w-none sm:pr-32'>
        {card.blurb}
      </p>
    )}
    <div
      className={clsx(
        'relative mt-[18px] sm:mt-3 overflow-hidden rounded-xl bg-[#FEFEFE]',
        size === 'wide'
          ? 'h-[460px] sm:h-[280px] lg:h-[520px]'
          : size === 'large'
            ? 'h-[460px] sm:h-[280px] lg:h-[520px]'
            : 'h-[380px] sm:h-[220px] lg:h-[380px]',
      )}
    >
      {card.mobileImage && (
        <Image
          src={card.mobileImage}
          alt={card.imageAlt}
          fill
          className='object-contain object-right-top sm:hidden'
          sizes='100vw'
        />
      )}
      <Image
        src={card.image}
        alt={card.imageAlt}
        fill
        className={clsx(
          card.mobileImage ? 'hidden sm:block' : '',
          size === 'wide'
            ? 'object-cover object-center sm:object-contain sm:object-right-top'
            : 'object-cover object-right-top',
        )}
        sizes={
          size === 'wide'
            ? '(max-width: 1024px) 100vw, 80vw'
            : size === 'large'
              ? '(max-width: 1024px) 100vw, 40vw'
              : '(max-width: 768px) 100vw, 33vw'
        }
      />
    </div>
  </button>
  );
};

const TabHeadline: FC<{ tab: (typeof HOME_PRODUCT_TABS)[ProductTabId] }> = ({ tab }) => (
  <h2
    className='mx-auto max-w-[694px] whitespace-pre-line text-center font-medium text-black'
    style={{
      fontSize: 'clamp(1.5rem, 2.5vw, 2.75rem)',
      lineHeight: 'clamp(2rem, 3.5vw, 3.5rem)',
    }}
  >
    {tab.headlineBefore}
    <span style={{ color: ACCENT_COLOR }}>{tab.headlineAccent}</span>
    {tab.headlineAfter}
  </h2>
);

interface ProductTabsSectionProps {
  activeTab: ProductTabId;
}

const ProductTabsSection: FC<ProductTabsSectionProps> = ({ activeTab }) => {
  const posthog = usePostHog();
  const [openCard, setOpenCard] = useState<HomeProductCard | null>(null);
  const tab = HOME_PRODUCT_TABS[activeTab];

  const handleOpen = (card: HomeProductCard) => {
    setOpenCard(card);
    posthog?.capture('homepage_product_card_opened', { card: card.title });
  };

  return (
    <>
      <FadeIn variant='up' delay={0.1}>
        {/* The hero audience selector is the single control for this section. */}
        <div className='mx-auto flex max-w-[694px] flex-col items-center'>
          <div className='w-full'>
            <TabHeadline tab={tab} />
          </div>
        </div>

        <div
          data-active-audience={activeTab}
          aria-live='polite'
          className='mx-auto mt-[26px] sm:mt-[77px] max-w-[1240px] space-y-5'
        >
          <div className='grid grid-cols-1 gap-5 lg:grid-cols-[724fr_496fr]'>
            <ProductCard card={tab.primaryRow[0]} size='large' onOpen={handleOpen} />
            <ProductCard card={tab.primaryRow[1]} size='large' onOpen={handleOpen} />
          </div>
          {tab.wideRow ? (
            <ProductCard card={tab.wideRow} size='wide' onOpen={handleOpen} />
          ) : (
            tab.secondaryRow && (
              <div className='grid grid-cols-1 gap-5 md:grid-cols-3'>
                {tab.secondaryRow.map((card, index) => (
                  <ProductCard key={`${card.href}-${index}`} card={card} size='small' onOpen={handleOpen} />
                ))}
              </div>
            )
          )}
        </div>
      </FadeIn>

      <ProductDetailModal
        card={openCard}
        tabId={openCard ? activeTab : null}
        demoVideoUrl={openCard ? HOME_TAB_DEMO_VIDEOS[activeTab] : undefined}
        onClose={() => setOpenCard(null)}
      />
    </>
  );
};

export default ProductTabsSection;
